import React from "react";
import { Form, Input, Button, Collapse, DatePicker, theme } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useResumeStore } from "../../store/resumeStore";
import { FIELD_SPELLCHECK_PROPS } from "./resumeQuality";
import useViewportWidth from "../../hooks/useViewportWidth";

const { Panel } = Collapse;
const { useToken } = theme;
const DATE_FORMAT = "DD/MM/YYYY";
const LEGACY_DATE_FORMATS = [
  DATE_FORMAT,
  "D/M/YYYY",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "YYYY-MM-DD",
  "MMM YYYY",
  "MMMM YYYY",
  "MM/YYYY",
  "M/YYYY",
  "YYYY",
];

dayjs.extend(customParseFormat);

function parseEditorDate(value) {
  if (typeof value !== "string" || !value.trim()) return null;

  const parsed = dayjs(value.trim(), LEGACY_DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

function getDatePlaceholder(value, fallback) {
  if (parseEditorDate(value)) return fallback;
  return typeof value === "string" && value.trim() ? value : fallback;
}

export default function EducationEditor() {
  const { token } = useToken();
  const viewportWidth = useViewportWidth();
  const resume = useResumeStore((s) => s.resume);
  const updateResume = useResumeStore((s) => s.updateResume);

  const education = Array.isArray(resume.education)
    ? resume.education
    : [];
  const languages = Array.isArray(resume.languages)
    ? resume.languages
    : [];
  const isMobile = viewportWidth <= 768;

  const addEducation = () => {
    const newItem = {
      institution: "",
      url: "",
      degree: "",
      field: "",
      from: "",
      to: "",
      score: "",
    };
    updateResume("education", [...education, newItem]);
  };

  const updateEducation = (index, field, value) => {
    const list = [...education];
    list[index] = {
      ...list[index],
      [field]: value,
    };
    updateResume("education", list);
  };

  const removeEducation = (index) => {
    const list = [...education];
    list.splice(index, 1);
    updateResume("education", list);
  };

  const addLanguage = () => {
    updateResume("languages", [...languages, ""]);
  };

  const updateLanguage = (index, value) => {
    const list = [...languages];
    list[index] = value;
    updateResume("languages", list);
  };

  const removeLanguage = (index) => {
    const list = [...languages];
    list.splice(index, 1);
    updateResume("languages", list);
  };

  return (
    <div>
      {/* Title */}
      <h3
        style={{
          color: token.colorTextHeading,
          // marginBottom: 16,
        }}
      >
        Education & Languages
      </h3>

      <Collapse
        accordion
        ghost
        expandIconPosition="end"
        defaultActiveKey={education.length > 0 ? ["0"] : ["languages"]}
        style={{
          background: "transparent",
        }}
      >
        {education.map((edu, index) => (
          <Panel
            key={index}
            header={
              <span
                style={{
                  color: token.colorTextHeading,
                  fontWeight: 500,
                }}
              >
                {edu.institution
                  ? `${edu.institution} - ${edu.degree || "Degree"}`
                  : `Education ${index + 1}`}
              </span>
            }
            extra={
              <DeleteOutlined
                onClick={(e) => {
                  e.stopPropagation();
                  removeEducation(index);
                }}
                style={{
                  color: token.colorError,
                  fontSize: 16,
                }}
              />
            }
          >
            <Form layout="vertical">
              <Form.Item label="Institution Name" required>
                <Input
                  value={edu.institution}
                  onChange={(e) =>
                    updateEducation(index, "institution", e.target.value)
                  }
                  {...FIELD_SPELLCHECK_PROPS}
                />
              </Form.Item>

              <Form.Item label="Institution URL (optional)">
                <Input
                  value={edu.url}
                  onChange={(e) =>
                    updateEducation(index, "url", e.target.value)
                  }
                  spellCheck={false}
                />
              </Form.Item>

              <Form.Item label="Type of Study (Degree)" required>
                <Input
                  placeholder="e.g. B.Tech, M.Tech, B.Sc"
                  value={edu.degree}
                  onChange={(e) =>
                    updateEducation(index, "degree", e.target.value)
                  }
                  {...FIELD_SPELLCHECK_PROPS}
                />
              </Form.Item>

              <Form.Item label="Functional Area / Field of Study">
                <Input
                  placeholder="e.g. Computer Science, Electronics"
                  value={edu.field}
                  onChange={(e) =>
                    updateEducation(index, "field", e.target.value)
                  }
                  {...FIELD_SPELLCHECK_PROPS}
                />
              </Form.Item>

              <Form.Item label="From - To" required>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder={getDatePlaceholder(
                      edu.from,
                      "From (DD/MM/YYYY)",
                    )}
                    value={parseEditorDate(edu.from)}
                    onChange={(_, dateString) =>
                      updateEducation(index, "from", dateString || "")
                    }
                  />
                  <DatePicker
                    style={{ width: "100%" }}
                    format={DATE_FORMAT}
                    placeholder={getDatePlaceholder(
                      edu.to,
                      "To (DD/MM/YYYY)",
                    )}
                    value={parseEditorDate(edu.to)}
                    onChange={(_, dateString) =>
                      updateEducation(index, "to", dateString || "")
                    }
                  />
                </div>
              </Form.Item>

              <Form.Item label="Score / Percentage / CGPA">
                <Input
                  placeholder="e.g. 8.3 CGPA or 72%"
                  value={edu.score}
                  onChange={(e) =>
                    updateEducation(index, "score", e.target.value)
                  }
                  {...FIELD_SPELLCHECK_PROPS}
                />
              </Form.Item>
            </Form>
          </Panel>
        ))}

        <Panel
          key="languages"
          header={
            <span
              style={{
                color: token.colorTextHeading,
                fontWeight: 500,
              }}
            >
              {languages.length > 0
                ? `Languages (${languages.filter(
                  (language) =>
                    typeof language === "string" && language.trim()
                ).length})`
                : "Languages"}
            </span>
          }
        >
          <Form layout="vertical">
            {languages.map((language, index) => (
              <Form.Item
                key={`language-${index}`}
                label={index === 0 ? "Language" : `Language ${index + 1}`}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
                    gap: 8,
                  }}
                >
                  <Input
                    placeholder="e.g. English, Spanish, German"
                    value={language}
                    onChange={(e) => updateLanguage(index, e.target.value)}
                    {...FIELD_SPELLCHECK_PROPS}
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeLanguage(index)}
                  />
                </div>
              </Form.Item>
            ))}
          </Form>

          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={addLanguage}
          >
            Add Language
          </Button>
        </Panel>
      </Collapse>

      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        style={{ marginTop: 16 }}
        onClick={addEducation}
      >
        Add Education
      </Button>
    </div>
  );
}
